/** @jsx jsx */
// tslint:disable:max-line-length
import { css, jsx } from '@emotion/react';
import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { TableList, TableTitleHeader } from '../../_shared/table-list';
import { Button } from '../../_shared/button';
import SharedStyles from '../../_shared/styles';
import { Alert, Layout, Menu, Input, message } from 'antd';
import { get, sortBy } from 'lodash';
import styles from './manage-profile.styles';
import { Org } from '../../_shared/interfaces/org';
import { Location } from '../../_shared/interfaces/location';
import { Select } from '../../_shared/select';
import { SelectValue } from 'antd/lib/select';
import {
  getAllMyOrgs,
  findAssocDentalOrgs,
  isHandpiece,
  userHasRole,
  sortAlphaNumeric,
} from '../../utils';
import { getEquipments } from '../../_shared/services/manage-equipment.service';
import {
  setHandpieceProfileOverride,
  getHandpieceProfilesForLocation,
  syncHandpiecesForLocation,
} from '../../_shared/services/handpiece-profiles.service';
import {
  IHandpieceProfile,
  IHandpieceDefaultsProfiles,
  IHandpiecePreset,
  humanReadableEndoSetting,
  humanReadableGearRatio,
  HandpieceGearRatio,
  HandpieceEndoSetting,
  HandpiecePresetKey,
  HandpieceProfileKey,
} from '../../_shared/interfaces/handpieceProfiles';
import { getOrgs } from '../../_shared/services/manage-orgs.service';
import { useStore } from 'react-redux';
import { AIcon as Icon } from '../../_shared/icon';

interface IRowItem {
  preset: IHandpiecePreset;
  profileKey: HandpieceProfileKey;
  presetKey: HandpiecePresetKey;
}

type SelectedProfile = {
  key: HandpieceProfileKey;
  profile: IHandpieceProfile;
};

// AER-1708: this guard used to read `[A-z0-9. ]`. `A-z` is not "letters" -- it
// is every character from `A` (65) through `z` (122), so it silently also
// admitted the six punctuation characters that sit between `Z` and `a`:
// [ \ ] ^ _ ` -- which is how `Profile_1` and `Profile^` were being accepted.
// Spelling both letter ranges out makes the class match the rule the error
// message below actually states.
const PROFILE_NAME_PATTERN = /^[A-Za-z0-9. ]{1,16}$/;

// AER-1708: neither of these rules is surfaced anywhere else in the screen --
// the 16-character cap is only visible as the input refusing further typing --
// so the message has to state both.
const PROFILE_NAME_RULE =
  'Profile name is limited to 16 characters, and may only contain letters, numbers, spaces and periods.';

// Returns the reason the name cannot be used, or undefined when it is valid.
const validateProfileName = (name: string): string | undefined => {
  if (!name) {
    return 'Profile name is required.';
  }
  return PROFILE_NAME_PATTERN.test(name) ? undefined : PROFILE_NAME_RULE;
};

const _ManageProfileComponent: React.FC<RouteComponentProps> = ({
  location,
  history,
}) => {
  const store = useStore();
  const selected = get(location, 'state.selected', undefined);

  //---- STATE
  const [userOrgs, setUserOrgs] = useState<Org[]>([]);
  const [selectedDentalOrg, setSelectedDentalOrg] = useState<Org | undefined>(
    selected
      ? selected.dentalOrg
      : userOrgs.length > 0
      ? userOrgs[0]
      : undefined
  );
  const [validLocations, setValidLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<
    Location | undefined
  >(
    selected
      ? selected.location
      : validLocations.length > 0
      ? validLocations[0]
      : undefined
  );

  const [profiles, setProfiles] = useState<
    IHandpieceDefaultsProfiles | undefined
  >(undefined);
  const [selectedPresetType, setSelectedPresetType] = useState<PresetType>(
    selected ? selected.presetType : 'prep'
  );
  const [presetsToDisplay, setPresetsToDisplay] = useState<IRowItem[]>([]);

  const [selectedProfile, setSelectedProfile] = useState<
    SelectedProfile | undefined
  >(
    selected
      ? { key: selected.profileKey, profile: selected.profile }
      : undefined
  );
  const [selectedProfileName, setSelectedProfileName] = useState<string>(
    selectedProfile ? selectedProfile.profile.name : 'N/A'
  );
  const [editingProfileName, setEditingProfileName] = useState<boolean>(false);
  // AER-1705: a rename now pushes the new name out to the handpieces, so the
  // confirm is no longer instantaneous and needs to be surfaced.
  const [savingProfileName, setSavingProfileName] = useState<boolean>(false);
  // AER-1708: why the last confirm was refused, so the editor can stay open
  // with what the user typed instead of throwing it away without explanation.
  const [profileNameError, setProfileNameError] = useState<string | undefined>(
    undefined
  );

  const [loading, setLoading] = useState<boolean>(true);

  //---- EFFECTS

  // AER-1588: surface one-shot router-state successMessage as a transient toast
  useEffect(() => {
    const successMessage = get(location, 'state.successMessage', undefined);
    if (successMessage) {
      message.success(successMessage);
    }
    // tslint:disable-next-line: react-hooks/exhaustive-deps
  }, []);

  // orgs need to be calculated at runtime in case
  // the user is associated with any SERVICE ORGS, we
  // need to flatted that down to related DENTAL ORGS,
  // we need the DENTAL ORG id to get related locations
  useEffect(() => {
    const asyncWork = async () => {
      const sState = store.getState() as any;
      const showAll = userHasRole([0, 1], sState);
      const serviceAndRegularOrgs: Org[] = showAll
        ? await getOrgs()
        : getAllMyOrgs(sState);

      // AER-1613: fetch equipment ONCE from the store and bucket handpieces by
      // dentalOrgId up front. The old code called getEquipments({dentalOrgId})
      // inside the per-org loop; each call re-ran parseResult over the entire
      // store-held equipment array (the whole equipment set for an admin /
      // role 0-1 account), so this loop did N full scans on the main thread and
      // produced Chrome's "Page Unresponsive" prompt. A single scan + lookup
      // map yields identical results because every call read the same array.
      const allEquipment = await getEquipments();
      const handpieceOrgIds = new Set<string>();
      for (const e of allEquipment) {
        if (e.dentalOrgId && isHandpiece(e)) {
          handpieceOrgIds.add(e.dentalOrgId);
        }
      }

      var final: Org[] = [];
      for (const someOrg of serviceAndRegularOrgs) {
        // this is a service org and we have to go a layer deeper
        if (someOrg.orgType === 0) {
          for (const org of findAssocDentalOrgs(someOrg.id, sState)) {
            if (
              handpieceOrgIds.has(org.id) &&
              !final.some(o => o.id === org.id)
            ) {
              final.push(org);
            }
          }
        } else {
          if (
            handpieceOrgIds.has(someOrg.id) &&
            !final.some(o => o.id === someOrg.id)
          ) {
            final.push(someOrg);
          }
        }
      }
      setUserOrgs(final);
    };

    asyncWork();
  }, []);

  // when orgs options change, auto-select the first (or un-set anything)
  useEffect(
    () => setSelectedDentalOrg(userOrgs.length > 0 ? userOrgs[0] : undefined),
    [userOrgs]
  );

  // when selectedDentalOrg changes, we need to update the list of valid Locations
  useEffect(() => {
    const fetchLocations = async () => {
      const sState = store.getState();
      if (selectedDentalOrg) {
        setLoading(true);
        const locations = get(sState, 'dash.locations', []) as Location[];
        const equipment = await getEquipments();
        const locationsForSelectedOrg = locations.filter(
          l =>
            selectedDentalOrg.id === l.orgId ||
            selectedDentalOrg.serviceOrgId === l.orgId
        );

        // AER-1613: collect location ids that have a handpiece in a single
        // pass instead of re-filtering the full store-held equipment array
        // once per location (the nested scan blocked the main thread for admin
        // accounts whose store holds the entire equipment set).
        const handpieceLocationIds = new Set<string | undefined>();
        for (const e of equipment) {
          if (e.locationId && isHandpiece(e)) {
            handpieceLocationIds.add(e.locationId);
          }
        }

        var final: Location[] = [];
        for (var o of locationsForSelectedOrg) {
          if (handpieceLocationIds.has(o.id)) {
            final.push(o);
          }
        }
        setValidLocations(final);
        if (final.length > 0) {
          setSelectedLocation(final[0]);
        }
        setLoading(false);
      } else {
        setValidLocations([]);
      }
    };

    fetchLocations();
  }, [selectedDentalOrg]);

  // when the selected location changes,
  // the profiles must be re-calculated
  useEffect(() => {
    const work = async (loc: Location) => {
      const profiles = await getHandpieceProfilesForLocation(loc);
      profiles && setProfiles(profiles);
    };

    selectedLocation && work(selectedLocation);
  }, [selectedLocation]);

  // when the profiles change, let's go ahead and auto-select the first (if there isn't already a selected profile)
  useEffect(() => {
    if (
      (!selectedProfile || !selectedProfile.profile) &&
      profiles &&
      profiles.profile1
    ) {
      setSelectedProfile({ key: 'profile1', profile: profiles.profile1 });
    } else if (
      selectedProfile &&
      selectedProfile.key &&
      profiles &&
      profiles[selectedProfile.key]
    ) {
      setSelectedProfile({
        key: selectedProfile.key,
        profile: profiles[selectedProfile.key],
      });
    }
  }, [profiles]);

  // when either the selected preset type (endo or prep) changes
  // or the selectedProfile changes, we gotta update the presets to display
  useEffect(() => {
    if (selectedProfile && selectedProfile.key && selectedProfile.profile) {
      if (selectedPresetType === 'endo') {
        setPresetsToDisplay([
          {
            presetKey: 'e1',
            profileKey: selectedProfile.key,
            preset: get(
              selectedProfile,
              `profile.endo_presets.e1`,
              get(profiles, 'endo_presets.e1') as any
            ) as any,
          },
          {
            presetKey: 'e2',
            profileKey: selectedProfile.key,
            preset: get(
              selectedProfile,
              `profile.endo_presets.e2`,
              get(profiles, 'endo_presets.e2') as any
            ) as any,
          },
          {
            presetKey: 'e3',
            profileKey: selectedProfile.key,
            preset: get(
              selectedProfile,
              `profile.endo_presets.e3`,
              get(profiles, 'endo_presets.e3') as any
            ) as any,
          },
        ]);
      } else {
        setPresetsToDisplay([
          {
            presetKey: 'p1',
            profileKey: selectedProfile.key,
            preset: get(
              selectedProfile,
              `profile.prep_presets.p1`,
              get(profiles, 'prep_presets.p1') as any
            ) as any,
          },
          {
            presetKey: 'p2',
            profileKey: selectedProfile.key,
            preset: get(
              selectedProfile,
              `profile.prep_presets.p2`,
              get(profiles, 'prep_presets.p2') as any
            ) as any,
          },
          {
            presetKey: 'p3',
            profileKey: selectedProfile.key,
            preset: get(
              selectedProfile,
              `profile.prep_presets.p3`,
              get(profiles, 'prep_presets.p3') as any
            ) as any,
          },
        ]);
      }

      setSelectedProfileName(selectedProfile.profile.name);
    } else {
      setSelectedProfileName('N/A');
    }
  }, [selectedPresetType, selectedProfile]);

  //---- UI HANDLERS

  const dentalOrgOptionSelected = useCallback(
    (value: SelectValue) => {
      const selected = userOrgs.find(o => o.id === value);
      selected && setSelectedDentalOrg(selected);
    },
    [userOrgs]
  );

  const locationOptionSelected = useCallback(
    (value: SelectValue) => {
      const selected = validLocations.find(l => l.id === value);
      selected && setSelectedLocation(selected);
    },
    [validLocations]
  );

  const profileSelected = useCallback(
    (profile: IHandpieceProfile, index: number) => {
      if (index > 9 || index < 0) return;
      const key = get(profile, 'key');
      if (key) {
        setSelectedProfile({ key, profile });
      }
    },
    [profiles, selectedLocation, selectedDentalOrg]
  );

  const editNameClicked = useCallback(() => {
    setProfileNameError(undefined);
    setEditingProfileName(true);
  }, []);

  const editNameCanceled = useCallback(() => {
    setProfileNameError(undefined);
    setSelectedProfileName(
      selectedProfile ? selectedProfile.profile.name : 'N/A'
    );
    setEditingProfileName(false);
  }, [selectedProfile]);

  // AER-1705: the profile name is part of the device configuration -- the
  // server writes `profileData.name` straight into the `set_cfg` payload it
  // sends to a handpiece -- so storing the location override is only half of a
  // rename. Without the fan-out below the handpieces keep serving the previous
  // name indefinitely, and the app and the devices silently disagree.
  const editNameConfirmed = useCallback(async () => {
    // AER-1708: validate before anything is applied, and on failure keep the
    // editor open with what the user typed. The old `else` branch handed
    // control to editNameCanceled(), which restored the previous name and
    // closed the editor, so the typing vanished with no message and no hint
    // that a length or character rule even existed.
    const nameError = validateProfileName(selectedProfileName);
    if (!nameError) {
      setProfileNameError(undefined);
      // Close the editor up front: the name is already applied locally and the
      // push below takes as long as the location has handpieces.
      setEditingProfileName(false);
      if (profiles && selectedProfile) {
        var mutable = profiles[selectedProfile.key];
        if (mutable) {
          mutable.name = selectedProfileName;
          setSelectedProfile({ key: selectedProfile.key, profile: mutable });
        }

        if (selectedLocation) {
          setSavingProfileName(true);
          try {
            const stored = await setHandpieceProfileOverride(
              selectedLocation,
              selectedProfile.key,
              selectedProfileName
            );
            if (!stored) {
              // Never push when the override did not persist, or the devices
              // would be re-sent the OLD name while the UI shows the new one.
              message.error('Profile name could not be saved.');
              return;
            }

            // Scope is the LOCATION (AER-1704): the config each handpiece
            // receives is derived from this location's overrides, so a
            // handpiece anywhere else would be sent settings never meant for
            // it.
            const result = await syncHandpiecesForLocation(selectedLocation);
            if (result.error) {
              message.error('Profile name updated, but could not be sent to handpiece unit(s).');
            } else if (result.failed.length > 0) {
              // AER-1722: `Failures will be re-attempted nightly.` used to end this
              // sentence. Nothing re-attempted anything -- there is no nightly job and the
              // retry table it referred to had no reader -- so the message talked the user
              // out of the only action that would have fixed the handpiece. What is true
              // is what replaced it: the units are recorded as out of sync and stay that
              // way until a push to them succeeds. AER-1723 adds the recovery, and it
              // fires when the device reconnects, so "nightly" will still be wrong then.
              message.error('Profile name updated, but could not be sent to some handpiece unit(s). They are marked out of sync.');
            } else {
              message.success('Profile name updated and sent to handpiece unit(s)');
            }
          } finally {
            setSavingProfileName(false);
          }
        }
      }
    } else {
      setProfileNameError(nameError);
    }
  }, [selectedProfile, selectedProfileName, profiles, selectedLocation]);

  const onNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event.target) {
      // AER-1708: drop the message as soon as they start correcting the name,
      // so a stale complaint is never left sitting under a field they fixed.
      setProfileNameError(undefined);
      setSelectedProfileName(event.target.value ? event.target.value : '');
    }
  }, []);

  const presetSelected = useCallback(
    (rowItem: IRowItem, _index: number) => {
      if (
        selectedDentalOrg &&
        selectedLocation &&
        selectedProfile &&
        selectedProfile.profile
      ) {
        // AER-1613: do NOT pass the entire Redux store through router history
        // state. For admin (role 0/1) accounts the store holds every org /
        // location / equipment record, so structured-cloning it into the
        // History API on click blocks the main thread (Chrome "Page
        // Unresponsive") and leaves a white screen. edit-preset never reads it.
        history.replace('/dashboard/editPreset', {
          profileKey: rowItem.profileKey,
          presetKey: rowItem.presetKey,
          preset: rowItem.preset,
          profile: selectedProfile.profile,
          org: selectedDentalOrg,
          location: selectedLocation,
        });
      }
    },
    [selectedLocation, selectedProfile, selectedDentalOrg]
  );

  const validProfiles = sortAlphaNumeric(
    Object.keys(profiles || {})
      .filter(k => k.startsWith('profile'))
      .map(k => get(profiles, k, {})),
    'key'
  ) as IHandpieceProfile[];

  //---- UI DECLARATIVE

  return (
    <div css={css(styles.pageContainer)}>
      <div css={css(styles.controlsContainer)}>
        {!!profiles && (
          <Layout.Sider width={200} css={css(styles.sideBar)}>
            <Menu
              mode="inline"
              defaultSelectedKeys={[get(validProfiles, '0.key') as any]}
              selectedKeys={
                selectedProfile ? [get(selectedProfile, 'key') as any] : []
              }
              style={{ height: '100%', borderRight: 0 }}
            >
              {validProfiles.map((profile, i) => {
                return (
                  <Menu.Item
                    key={get(profile, 'key')}
                    onClick={() => profile && profileSelected(profile, i)}
                  >
                    {get(profile, 'name', get(profile, 'key', 'n/a'))}
                  </Menu.Item>
                );
              })}
            </Menu>
          </Layout.Sider>
        )}

        <div css={css(styles.presetsContainer)}>
          <div css={css(styles.presetTypeToggle)}>
            <Button
              title="Prep"
              outline={selectedPresetType !== 'prep'}
              onClick={() => setSelectedPresetType('prep')}
            />
            <Button
              title="Endo"
              outline={selectedPresetType !== 'endo'}
              onClick={() => setSelectedPresetType('endo')}
            />
          </div>

          <div css={css(styles.dropDownSelections)}>
            {userOrgs.length > 0 && (
              <div css={css(styles.dropDownItem)}>
                <label css={css(styles.dropDownLabel)}>Select Dental Org</label>
                <Select
                  mode={undefined}
                  css={css(styles.dropDownSelect)}
                  placeholder={'Dental Org'}
                  value={selectedDentalOrg ? selectedDentalOrg.name : undefined}
                  onChange={dentalOrgOptionSelected}
                  filterOption={(input, option) => {
                    const name = option!.props.children as string;
                    if (name)
                      return (
                        name.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      );
                    return true;
                  }}
                >
                  {userOrgs.map((item, i) => (
                    <Select.Option key={i} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {validLocations.length > 0 && (
              <div css={css(styles.dropDownItem)}>
                <label css={css(styles.dropDownLabel)}>Select Location</label>
                <Select
                  mode={undefined}
                  css={css(styles.dropDownSelect)}
                  placeholder={'Locations'}
                  value={selectedLocation ? selectedLocation.name : undefined}
                  onChange={locationOptionSelected}
                  filterOption={(input, option) => {
                    const name = option!.props.children as string;
                    if (name)
                      return (
                        name.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      );
                    return true;
                  }}
                >
                  {validLocations.map((item, i) => (
                    <Select.Option key={i} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}
          </div>

          <div css={css(styles.table)}>
            <div css={css(`flex-direction: row`)}>
              {!editingProfileName && (
                <TableTitleHeader>{selectedProfileName}</TableTitleHeader>
              )}
              {editingProfileName && (
                <Input
                  maxLength={16}
                  onChange={onNameChange}
                  value={selectedProfileName}
                  status={profileNameError ? 'error' : undefined}
                  css={css(`width: 12%`)}
                />
              )}

              <div css={css(styles.nameControls)}>
                {/* AER-1705: hold the pencil back while the previous rename is
                    still being pushed to the handpieces, so two fan-outs can
                    not overlap. */}
                {!editingProfileName && !savingProfileName && (
                  <Icon onClick={() => editNameClicked()} type={'edit'} />
                )}

                {savingProfileName && <Icon type="loading" />}

                {editingProfileName && (
                  <Icon
                    onClick={() => editNameConfirmed()}
                    type={'check-circle'}
                  />
                )}
                {editingProfileName && (
                  <Icon
                    onClick={() => editNameCanceled()}
                    css={css(`margin-left: 5px`)}
                    type={'close-circle'}
                  />
                )}
              </div>
            </div>

            {/* AER-1708: the rename rules are stated nowhere else, so a refused
                name has to explain itself here rather than just reverting. */}
            {!!profileNameError && (
              <p css={css(styles.nameError)}>{profileNameError}</p>
            )}

            {loading && <Icon css={css('margin-left: 4px;')} type="loading" />}

            {!loading && (
              <div>
                <TableList
                  title={'Presets'}
                  data={presetsToDisplay as never}
                  rowKey={r => `${JSON.stringify(r)}`}
                  loading={loading}
                  columns={PresetColumns.filter(c => {
                    if (selectedPresetType === 'endo') return true;
                    const key = Array.isArray(c.dataIndex)
                      ? c.dataIndex.join('.')
                      : c.dataIndex;
                    return (
                      key !== 'preset.torque_threshold' &&
                      key !== 'preset.endo_mode_enum_raw'
                    );
                  })}
                  includeCount={false}
                  showPagination={false}
                  canSelectRows={false}
                  implementScroll={false}
                  rowClick={presetSelected}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// antd v5+ Table no longer supports dot-notation dataIndex strings; use
// array paths instead (AER-1465 regression).
const PresetColumns = [
  {
    title: 'Preset Name',
    dataIndex: ['preset', 'name'],
    width: '11.1%',
  },
  {
    title: 'Gear Ratio',
    dataIndex: ['preset', 'gear_ratio_enum_raw'],
    width: '11.1%',
    render: (gr: HandpieceGearRatio) => (
      <span>{humanReadableGearRatio(gr)}</span>
    ),
  },
  {
    title: 'RPM',
    dataIndex: ['preset', 'rpm'],
    width: '9%',
  },
  {
    title: 'Rotation',
    dataIndex: ['preset', 'rotation_clockwise'],
    width: '11.1%',
    render: (clockwise: boolean) => (
      <span>{clockwise ? 'clockwise' : 'counter-clockwise'}</span>
    ),
  },
  {
    title: 'Torque',
    dataIndex: ['preset', 'torque_threshold'],
    width: '9%',
  },
  {
    title: 'Endo Setting',
    dataIndex: ['preset', 'endo_mode_enum_raw'],
    width: '11.1%',
    render: (em: HandpieceEndoSetting) => (
      <span>{humanReadableEndoSetting(em)}</span>
    ),
  },
];

type PresetType = 'endo' | 'prep';

export const ManageProfileComponent = withRouter(_ManageProfileComponent);
